import {
  Button,
  Card,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableDock,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  Job,
  ReportShipData,
  ReportTemplate,
  useSetSelectionData,
} from '@ais-ui/websockets';
import { ColumnDef, flexRender } from '@tanstack/react-table';
import { PiTable } from 'react-icons/pi';
import React, { useEffect, useRef } from 'react';
import { FilterMenu } from './Filter';
import { TableFilter } from './filterTypes';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useFetchOverlayData } from '../useMapReportOverlay';
import { useShipTable } from './useShipTable';
import { useGetReportTableData } from './useGetReportTableData';

import { ReportsOptionsMenuItems } from './TableOptions';
import { useReportContext } from '../ReportContext';
import { useMapPanelContext } from '../../Map/MapPanel';
import { useTableColumnsWithActions } from './hooks/useTableColumnsWithActions';
import { useSelectableReportType } from './hooks/useSelectableReportType';
import { EmptyListTable } from '../../../components/EmptyListTable';
import { useTableDensity } from '../../utils/useTableDensity';

interface ReportShipTableProps {
  shipData: ReportShipData[] | undefined;
  className: string;
  jobId: string | undefined;
  reportType: string | null;
  isLoading: boolean;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filters: TableFilter[];
  className?: string;
  jobId: string | undefined;
  isSelectableReportType: boolean;
  isLoading: boolean;
  job?: Job;
  reportTemplate?: ReportTemplate;
}

export const CollapsedTable = () => {
  const { messages } = useLocalization();
  const { setTableExpanded } = useMapPanelContext();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          onClick={() => setTableExpanded(true)}
          variant="outline"
          size="icon"
        >
          <PiTable className="h-6 w-6" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {messages.reportsTableExpandButtonTooltip}
      </TooltipContent>
    </Tooltip>
  );
};

function DataTable<TData, TValue>({
  columns,
  data,
  filters,
  className,
  jobId,
  isSelectableReportType,
  isLoading,
  job,
  reportTemplate,
}: DataTableProps<TData, TValue>) {
  const { messages } = useLocalization();
  const { tableExpanded, setTableExpanded } = useMapPanelContext();
  const { densityClasses } = useTableDensity();
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const {
    table,
    rowsPerPage,
    appliedFilters,
    filteredData,
    onApplyFilter,
    onRemoveFilter,
    page,
    setPage,
    selectedRowIds,
    setSelectedRowIds,
  } = useShipTable(data, columns, isSelectableReportType);

  const fetchOverlayData = useFetchOverlayData();
  const { mutate } = useSetSelectionData(fetchOverlayData);
  const { showTracks } = useReportContext();

  useEffect(() => {
    const selectedIds = Object.keys(selectedRowIds)
      .map((id) => parseInt(id, 10))
      .filter((id) => selectedRowIds[id])
      .map((id) => ({ id }));

    if (!jobId) {
      return;
    }

    if (selectedIds.length > 0) {
      mutate({
        jobId: jobId,
        selections: selectedIds,
        show: showTracks,
        show_only_selected: true,
        show_sog_distribution: false,
        show_decimal_knots: false,
        ignore_sog_of_zero: false,
      });
    } else {
      mutate({
        jobId: jobId,
        selections: [],
        show: showTracks,
        show_only_selected: false,
        show_sog_distribution: false,
        show_decimal_knots: false,
        ignore_sog_of_zero: false,
      });
    }
  }, [selectedRowIds, mutate, jobId, showTracks]);

  if (!tableExpanded) {
    return null;
  }

  return (
    <Card
      variant="elevated"
      className={cn(
        'relative flex flex-col h-fit w-full transition-all group/data-table px-1 pb-4 pt-1',
        className,
      )}
    >
      <TableDock selectedCount={Object.keys(selectedRowIds).length}>
        <TableDock.Identity
          icon={<PiTable />}
          title={reportTemplate?.tplName ?? 'Report'}
          typeTag="Report"
        />
        <TableDock.State>
          {Object.entries(appliedFilters).map(([column, appliedFilter]) => (
            <TableDock.FilterChip
              key={column}
              label={`${column} ${appliedFilter.operator} ${appliedFilter.value}`}
              onRemove={() => onRemoveFilter(column)}
            />
          ))}
          {!isLoading && Object.keys(selectedRowIds).length > 0 && (
            <TableDock.SelectionChip
              count={Object.keys(selectedRowIds).length}
              onClear={() => setSelectedRowIds({})}
            />
          )}
          <Popover>
            <PopoverTrigger asChild>
              <TableDock.AddFilter
                label={messages.reportsTableFilterButtonText}
              />
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start">
              <FilterMenu onApplyFilter={onApplyFilter} filters={filters} />
            </PopoverContent>
          </Popover>
        </TableDock.State>
        <TableDock.Controls>
          <TableDock.Actions resultCount={filteredData.length}>
            <ReportsOptionsMenuItems />
          </TableDock.Actions>
          {!isLoading && data.length > 0 && (
            <>
              <TableDock.Divider />
              <TableDock.Pager
                from={filteredData.length === 0 ? 0 : page * rowsPerPage + 1}
                to={Math.min((page + 1) * rowsPerPage, filteredData.length)}
                total={data.length}
                matched={filteredData.length}
                onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
                onNext={() =>
                  setPage((prev) =>
                    Math.min(
                      prev + 1,
                      Math.ceil(filteredData.length / rowsPerPage) - 1,
                    ),
                  )
                }
                prevDisabled={page === 0}
                nextDisabled={
                  page >= Math.ceil(filteredData.length / rowsPerPage) - 1
                }
              />
            </>
          )}
          <TableDock.Divider />
          <TableDock.Close onClick={() => setTableExpanded(false)} />
        </TableDock.Controls>
      </TableDock>
      <div className="flex overflow-hidden">
        <ScrollArea
          infiniteScrollRef={viewportRef}
          className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
        >
          <Table container={false}>
            <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => {
                    const isActionsColumn =
                      header.id === 'actions' || header.column.id === 'actions';
                    return (
                      <TableHead
                        key={header.id}
                        style={{
                          width: isActionsColumn
                            ? '50px'
                            : `${header.getSize()}px`,
                          maxWidth: isActionsColumn
                            ? '50px'
                            : `${header.column.columnDef.maxSize || header.getSize()}px`,
                          minWidth: isActionsColumn
                            ? '50px'
                            : `${header.column.columnDef.minSize || 60}px`,
                        }}
                        className={cn('truncate', densityClasses.tableHeader)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            {isLoading ? (
              <TableBody>
                <TableRow className="border-0">
                  <TableCell colSpan={columns.length}>
                    <div className="flex justify-center items-center min-h-[200px]">
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className="odd:bg-muted/50"
                    >
                      {row.getVisibleCells().map((cell) => {
                        const isActionsColumn = cell.column.id === 'actions';
                        return (
                          <TableCell
                            key={cell.id}
                            style={{
                              width: isActionsColumn
                                ? '50px'
                                : `${cell.column.getSize()}px`,
                              maxWidth: isActionsColumn
                                ? '50px'
                                : `${cell.column.columnDef.maxSize || cell.column.getSize()}px`,
                              minWidth: isActionsColumn
                                ? '50px'
                                : `${cell.column.columnDef.minSize || 60}px`,
                            }}
                            className={cn(densityClasses.tableCell)}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <div className="flex justify-center items-center min-h-[100px]">
                        <EmptyListTable
                          srcImage="empty_list_bro.avif"
                          entity="result"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </Card>
  );
}

export const ReportShipTable = React.memo(
  ({
    shipData,
    className,
    jobId,
    reportType,
    isLoading,
  }: ReportShipTableProps) => {
    const { filterByMapView, selectedJob, selectedReportTemplate } =
      useReportContext();

    const { columns, filters } = useTableColumnsWithActions({ shipData });

    useGetReportTableData(jobId, filterByMapView);

    const { isSelectableReportType } = useSelectableReportType(reportType);

    // Initialize with empty array if shipData is null/undefined to show loading state
    const safeShipData = shipData ?? [];

    return (
      <DataTable
        columns={columns}
        data={safeShipData}
        filters={filters}
        className={className}
        jobId={jobId}
        isSelectableReportType={isSelectableReportType}
        isLoading={isLoading}
        job={selectedJob as Job}
        reportTemplate={selectedReportTemplate as ReportTemplate}
      />
    );
  },
);

ReportShipTable.displayName = 'ReportShipTable';
